import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Rocket, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

const STORAGE_KEY = "cse_beta_notice_dismissed";

/**
 * Beta notice banner shown on the Dashboard during the closed beta
 * (6-12 months). All authenticated users have full Premium access for
 * free during this period.
 *
 * Visibility rules:
 *  - Hidden for admins.
 *  - Hidden once the user clicks the X button — preference stored in
 *    localStorage permanently (`cse_beta_notice_dismissed = "1"`) and
 *    never shown again on subsequent loads.
 */
const WelcomeBanner = () => {
  const { user, isAdmin } = useAuth();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(STORAGE_KEY) === "1");
    } catch (_e) {
      setDismissed(false);
    }
  }, []);

  if (!user) return null;
  if (isAdmin) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_e) {/* ignore */}
    setDismissed(true);
  };

  return (
    <Card
      className="border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/5"
      data-testid="beta-notice-banner"
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2.5 min-w-0">
            <Rocket className="h-5 w-5 shrink-0 text-[hsl(var(--info))] mt-0.5" />
            <div className="space-y-0.5 min-w-0">
              <p className="text-sm font-semibold" data-testid="beta-notice-title">
                CSE Investor Beta
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                You have full access to all features during our beta period.
                All features are completely free.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              aria-label="Dismiss beta notice"
              data-testid="beta-notice-dismiss"
            >
              <X className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only sm:ml-1">Dismiss</span>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WelcomeBanner;
